import { createContext, useContext, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { resolveValue, useToaster, type Toast } from 'react-hot-toast'
import { toastStackLayout, TOAST_STACK, type ToastPose } from './toastStackLayout'
import './AppToaster.scss'

/** Seit wann Zeiger oder Fokus im Stapel stehen (dann läuft keine Zeit) — für den Countdown am Knopf. */
const ToastPausedAtContext = createContext<number | null>(null)
export const useToastPausedAt = () => useContext(ToastPausedAtContext)

/** Sekunden, die ein Toast noch steht — `null`, wenn er keine Dauer kennt. */
export function secondsLeft(t: Pick<Toast, 'createdAt' | 'duration' | 'pauseDuration'>, pausedAt: number | null, now: number): number | null {
  if (t.createdAt == null || t.duration == null || !Number.isFinite(t.duration)) return null
  const endsAt = t.createdAt + t.duration + (t.pauseDuration ?? 0)
  return Math.max(0, Math.ceil((endsAt - (pausedAt ?? now)) / 1000))
}

/**
 * Die natürliche Höhe einer Karte. Hinter dem vordersten ist sie auf dessen Höhe gekürzt — dann
 * zählt ihr Inhalt (`.toast-card__body` samt Innenabstand), nicht ihr Kasten.
 */
function naturalHeight(card: HTMLElement): number {
  const body = card.querySelector<HTMLElement>('.toast-card__body')
  if (!body) return card.scrollHeight
  const style = getComputedStyle(card)
  return body.offsetHeight + parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0')
}

/**
 * **Die Toasts unten rechts, als Stapel** (#223, Prototyp `tools/toast-layout-prototype`,
 * Variante F; oben rechts und unten mittig in der Dev-App verworfen: Sven, 04.10.2026). Der neueste liegt vorn,
 * ältere schauen oben 12 px heraus; überfahren fächert der Stapel nach oben auf und die Zeit
 * steht. Neue wachsen herein (90 % → 100 %, von 8 px tiefer).
 *
 * Zustand und Zeit kommen aus react-hot-toast (`useToaster`); die Lage rechnet
 * {@link toastStackLayout}. Die Inhalte setzen `showSuccessToast`, `showUndoToast`,
 * `showErrorToast` und `showInfoToast` — einmal in `App.tsx` eingebunden.
 */
export function AppToaster() {
  const { toasts, handlers } = useToaster({ duration: 5000 })
  const [pausedAt, setPausedAt] = useState<number | null>(null)
  /*
    Die Zeit steht, solange der Zeiger darüber ist ODER der Fokus in einem Toast steht (`focusAction`,
    Tab) — sonst verschwände der Knopf unter dem Enter. Außer in einer Karte mit `runsWhileFocused`
    (Verschieben, #264): Die geht trotzdem; wandert der Fokus in sie hinein, läuft die Zeit wieder.
  */
  const pauses = useRef({ hover: false, focus: false })
  const pause = (reason: 'hover' | 'focus', on: boolean) => {
    const was = pauses.current.hover || pauses.current.focus
    pauses.current[reason] = on
    const now = pauses.current.hover || pauses.current.focus
    if (!was && now) { setPausedAt(Date.now()); handlers.startPause() }
    if (was && !now) { setPausedAt(null); handlers.endPause() }
  }
  const expanded = pausedAt !== null
  const items = useRef(new Map<string, HTMLDivElement>())
  // Wer gerade geht, behält seine letzte Lage und blendet dort aus.
  const lastPose = useRef(new Map<string, ToastPose>())

  const live = toasts.filter(t => t.visible) // neueste zuerst
  const { items: poses, total } = toastStackLayout(live.map(t => t.height ?? 0), { ...TOAST_STACK, expanded })
  live.forEach((t, i) => lastPose.current.set(t.id, poses[i]!))

  useLayoutEffect(() => {
    for (const t of toasts) {
      const card = items.current.get(t.id)?.firstElementChild as HTMLElement | null
      if (!card) continue
      const height = naturalHeight(card)
      if (height > 0 && height !== t.height) handlers.updateHeight(t.id, height)
    }
    for (const id of lastPose.current.keys()) {
      if (!toasts.some(t => t.id === id)) lastPose.current.delete(id)
    }
  })

  /*
    **An `document.body`, nicht dort, wo `App` ihn rendert** (#270). Die App liegt im Fensterrahmen in
    `.window-content` — `z-index: 4`, ein eigener Stapel. Darin galt das 9999 des Toasters nur innen,
    und die Kontur der Inhaltskarte (`.union-border-slot`, 5, `window.scss`) zog ihre Linie über den
    Toast. An `body` steht er im Stapel des Dokuments. Kontext reist durch das Portal mit; Thema und
    Lebensbereich hängen an `<html>`.
  */
  return createPortal(
    <ToastPausedAtContext.Provider value={pausedAt}>
      <div
        className={`app-toaster${expanded ? ' app-toaster--expanded' : ''}${live.length ? ' app-toaster--busy' : ''}`}
        style={{ '--toaster-height': `${total}px` } as CSSProperties}
        onMouseEnter={() => pause('hover', true)}
        onMouseLeave={() => pause('hover', false)}
        onFocus={(e) => pause('focus', !(e.target as Element).closest('[data-runs-while-focused]'))}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) pause('focus', false) }}
      >
        {toasts.map((t) => {
          const index = live.indexOf(t)
          const pose = index >= 0 ? poses[index]! : lastPose.current.get(t.id) ?? { y: 0, scale: 1, height: t.height ?? 0, opacity: 0, behind: false }
          const classes = ['app-toaster__item']
          if (pose.behind) classes.push('app-toaster__item--behind')
          if (!t.visible) classes.push('app-toaster__item--leaving')
          if (!t.height) classes.push('app-toaster__item--unmeasured')
          return (
            <div
              key={t.id}
              ref={(el) => { if (el) items.current.set(t.id, el); else items.current.delete(t.id) }}
              className={classes.join(' ')}
              style={{
                '--toast-y': `${pose.y}px`,
                '--toast-scale': pose.scale,
                '--toast-opacity': pose.opacity,
                '--toast-height': `${pose.height}px`,
                zIndex: index >= 0 ? 100 - index : 0,
              } as CSSProperties}
              {...t.ariaProps}
            >
              {resolveValue(t.message, t)}
            </div>
          )
        })}
      </div>
    </ToastPausedAtContext.Provider>,
    document.body,
  )
}
